/**
 * "Duo" — three-in-a-row for 2 players on 2 devices (local multiplayer).
 *
 * PURE reducer with host authority: the canonical state lives on the
 * host device; the guest only sends intents (`move`) and renders the
 * state the host broadcasts after each change. Thus there are no conflicts or
 * need to sync a DB: the state is ephemeral (channel memory).
 *
 * Didactic (ages 2-6): take turns and wait for the other's turn; no punishment
 * (an invalid move is simply ignored) and infinite rematch.
 */

export type DuoPlayer = "host" | "guest";

export type DuoCell = DuoPlayer | null;

export type DuoStatus = "playing" | "won" | "draw";

export interface DuoState {
  /** 9 cells, rows left to right and top to bottom. */
  cells: DuoCell[];
  turn: DuoPlayer;
  status: DuoStatus;
  winner: DuoPlayer | null;
  /** Indices of the winning line (to highlight it on both sides). */
  line: number[] | null;
  /** 1-based round; the rematch alternates who starts. */
  round: number;
  /** Who started THIS round (to compute the next one). */
  starter: DuoPlayer;
}

const LINES: readonly (readonly [number, number, number])[] = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

export function createDuoState(round = 1, starter: DuoPlayer = "host"): DuoState {
  return {
    cells: Array<DuoCell>(9).fill(null),
    turn: starter,
    status: "playing",
    winner: null,
    line: null,
    round,
    starter,
  };
}

export type DuoMoveResult =
  | { ok: true; state: DuoState }
  | { ok: false; reason: "not-your-turn" | "cell-taken" | "game-over" | "bad-cell" };

/**
 * Applies `player`'s move in `cell`. Returns the NEW state (the
 * previous one is not mutated) or the rejection reason — the host ignores rejections without
 * punishment and does NOT broadcast anything (the state did not change).
 */
export function applyDuoMove(state: DuoState, player: DuoPlayer, cell: number): DuoMoveResult {
  if (state.status !== "playing") {
    return { ok: false, reason: "game-over" };
  }
  if (!Number.isInteger(cell) || cell < 0 || cell > 8) {
    return { ok: false, reason: "bad-cell" };
  }
  if (state.turn !== player) {
    return { ok: false, reason: "not-your-turn" };
  }
  if (state.cells[cell] !== null) {
    return { ok: false, reason: "cell-taken" };
  }

  const cells = [...state.cells];
  cells[cell] = player;

  const line = LINES.find((l) => l.every((i) => cells[i] === player)) ?? null;
  if (line) {
    return {
      ok: true,
      state: { ...state, cells, status: "won", winner: player, line: [...line] },
    };
  }
  if (cells.every((c) => c !== null)) {
    return { ok: true, state: { ...state, cells, status: "draw", winner: null, line: null } };
  }
  return {
    ok: true,
    state: { ...state, cells, turn: player === "host" ? "guest" : "host" },
  };
}

/** Rematch: new round with a clean board and the OTHER player starting. */
export function nextDuoRound(state: DuoState): DuoState {
  const starter: DuoPlayer = state.starter === "host" ? "guest" : "host";
  return createDuoState(state.round + 1, starter);
}

function isPlayer(v: unknown): v is DuoPlayer {
  return v === "host" || v === "guest";
}

/** Structural guard: allows indexing an unknown without type assertions. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Validates a state received over the channel (the guest does not trust the wire). */
export function isDuoState(value: unknown): value is DuoState {
  if (!isRecord(value)) {
    return false;
  }
  const s = value;
  return (
    Array.isArray(s.cells) &&
    s.cells.length === 9 &&
    s.cells.every((c) => c === null || isPlayer(c)) &&
    isPlayer(s.turn) &&
    (s.status === "playing" || s.status === "won" || s.status === "draw") &&
    (s.winner === null || isPlayer(s.winner)) &&
    (s.line === null || (Array.isArray(s.line) && s.line.every((i) => Number.isInteger(i)))) &&
    typeof s.round === "number" &&
    isPlayer(s.starter)
  );
}
